import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import BasicInfoStep from "./BasicInfoStep";
import AccountRegionStep from "./AccountRegionStep";
import NamespacesStep from "./NamespacesStep";
import ReviewStep from "./ReviewStep";
import { Circle, CheckCircle, X, AlertTriangle } from "lucide-react";

export default function DashboardForm({
  dashboards,
  isAddingDashboard,
  isEditingDashboard,
  currentStep,
  setCurrentStep,
  dashboardData,
  setDashboardData,
  regions,
  awsAccounts,
  fetchNamespaces,
  fetchMetrics,
  fetchResourceNames,
  onSave,
  onCancel,
}) {
  const [availableNamespaces, setAvailableNamespaces] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [availableResourceNames, setAvailableResourceNames] = useState([]);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [collapsedNamespaces, setCollapsedNamespaces] = useState([]);
  // State for namespace being currently configured
  const [currentNamespace, setCurrentNamespace] = useState({
    name: "",
    metrics: [],
    resourceNames: []
  });
  // Add state for current alarm configs
  const [currentAlarmConfigs, setCurrentAlarmConfigs] = useState({});
  const [isLoadingNamespaces, setIsLoadingNamespaces] = useState(false);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [isLoadingResourceNames, setIsLoadingResourceNames] = useState(false);

  const steps = [
    { id: 1, title: "Basic Info" },
    { id: 2, title: "Account & Region" },
    { id: 3, title: "Namespaces & Metrics" },
    { id: 4, title: "Review" },
  ];

  const checkDashboardExists = (accountNo, region, currentDashboardId = null) => {
  const existingDashboard = dashboards.find(dashboard => 
    dashboard.accountNo === accountNo && 
    dashboard.region === region &&
    dashboard.id !== currentDashboardId // Exclude current dashboard when editing
  );
  return existingDashboard;
};
  // When account or region changes, fetch namespaces
  useEffect(() => {
    if (dashboardData.accountNo && dashboardData.region) {
      setIsLoadingNamespaces(true);
      fetchNamespaces(dashboardData.accountNo, dashboardData.region)
        .then(namespaces => {
          setAvailableNamespaces(namespaces);
          setIsLoadingNamespaces(false);
        })
        .catch(err => {
          setShowError(true);
          setErrorMessage("Failed to fetch namespaces. Please try again.");
          setIsLoadingNamespaces(false);
        });
    }
  }, [dashboardData.accountNo, dashboardData.region, fetchNamespaces]);

  // When namespace changes, fetch metrics and resource names
  useEffect(() => {
    if (currentNamespace.name && dashboardData.accountNo && dashboardData.region) {
      // Fetch metrics
      setIsLoadingMetrics(true);
      fetchMetrics(dashboardData.accountNo, dashboardData.region, currentNamespace.name)
        .then(metrics => {
          setAvailableMetrics(metrics);
          setIsLoadingMetrics(false);
        })
        .catch(err => {
          setShowError(true);
          setErrorMessage("Failed to fetch metrics. Please try again.");
          setIsLoadingMetrics(false);
        });

      // Fetch resource names
      setIsLoadingResourceNames(true);
      fetchResourceNames(dashboardData.accountNo, dashboardData.region, currentNamespace.name)
        .then(resources => {
          setAvailableResourceNames(resources);
          setIsLoadingResourceNames(false);
        })
        .catch(err => {
          setShowError(true);
          setErrorMessage("Failed to fetch resource names. Please try again.");
          setIsLoadingResourceNames(false);
        });
    }
  }, [currentNamespace.name, dashboardData.accountNo, dashboardData.region, fetchMetrics, fetchResourceNames]);

  // Load existing alarm configs when editing a namespace
  useEffect(() => {
    if (currentNamespace.name) {
      const existingNamespace = dashboardData.namespaces.find(
        ns => ns.name === currentNamespace.name
      );

      if (existingNamespace && existingNamespace.alarmConfigs) {
        setCurrentAlarmConfigs(existingNamespace.alarmConfigs);
      } else {
        setCurrentAlarmConfigs({});
      }
    }
  }, [currentNamespace.name, dashboardData.namespaces]);

  // Handle dashboard data change
  const handleDashboardDataChange = (field, value) => {
    // Prevent editing of name, accountNo, and region when editing dashboard
    if (isEditingDashboard && (field === 'name' || field === 'accountNo' || field === 'region')) {
      return;
    }

    setDashboardData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Reset namespaces when region changes (only for new dashboards)
    if (!isEditingDashboard && (field === "region" || field === "accountNo")) {
      setDashboardData((prev) => ({
        ...prev,
        namespaces: [],
      }));
    }
  };

  // Handle namespace selection
  const handleNamespaceChange = (e) => {
    const selectedNamespace = e.target.value;
    const existingNamespace = dashboardData.namespaces.find(
      ns => ns.name === selectedNamespace
    );

    if (existingNamespace) {
      // If namespace already exists, load its metrics, resources, and alarm configs
      setCurrentNamespace({
        name: existingNamespace.name,
        metrics: [...existingNamespace.metrics],
        resourceNames: [...existingNamespace.resourceNames]
      });

      // Load existing alarm configs
      if (existingNamespace.alarmConfigs) {
        setCurrentAlarmConfigs({ ...existingNamespace.alarmConfigs });
      } else {
        setCurrentAlarmConfigs({});
      }
    } else {
      // Otherwise start fresh
      setCurrentNamespace({
        name: selectedNamespace,
        metrics: [],
        resourceNames: []
      });
      setCurrentAlarmConfigs({});
    }
  };

  // Handle metric toggle
  const handleMetricToggle = (metric) => {
    setCurrentNamespace((prev) => {
      const isSelected = prev.metrics.includes(metric);
      return {
        ...prev,
        metrics: isSelected
          ? prev.metrics.filter((m) => m !== metric)
          : [...prev.metrics, metric],
      };
    });

    // Initialize alarm config for the metric if it doesn't exist
    if (!currentAlarmConfigs[metric]) {
      setCurrentAlarmConfigs(prev => ({
        ...prev,
        [metric]: {
          enabled: false,
          threshold: "0",
          statistic: "Average",
          comparisonOperator: "GreaterThanOrEqualToThreshold"
        }
      }));
    }
  };

  // Update alarm configuration
  const updateAlarmConfig = (metricName, field, value) => {
    setCurrentAlarmConfigs(prev => ({
      ...prev,
      [metricName]: {
        ...(prev[metricName] || {
          enabled: false,
          threshold: "0",
          statistic: "Average",
          comparisonOperator: "GreaterThanOrEqualToThreshold"
        }),
        [field]: value
      }
    }));
  };

  // Handle resource name toggle
  const handleResourceNameToggle = (resourceName) => {
    setCurrentNamespace((prev) => {
      const isSelected = prev.resourceNames.includes(resourceName);
      return {
        ...prev,
        resourceNames: isSelected
          ? prev.resourceNames.filter((r) => r !== resourceName)
          : [...prev.resourceNames, resourceName],
      };
    });
  };

  // Add or update namespace to dashboard
  const addNamespaceToDashboard = () => {
    if (!currentNamespace.name || currentNamespace.metrics.length === 0) {
      setShowError(true);
      setErrorMessage("Please select a namespace and at least one metric");
      return;
    }

    // Filter alarm configs to only include selected metrics
    // Filter alarm configs to only include selected metrics with enabled alarms
const filteredAlarmConfigs = {};
currentNamespace.metrics.forEach(metric => {
  if (currentAlarmConfigs[metric] && currentAlarmConfigs[metric].enabled) {
    filteredAlarmConfigs[metric] = currentAlarmConfigs[metric];
  }
});

    const namespaceToAdd = {
      ...currentNamespace,
      alarmConfigs: filteredAlarmConfigs
    };

    const existingIndex = dashboardData.namespaces.findIndex(
      ns => ns.name === currentNamespace.name
    );

    if (existingIndex >= 0) {
      // Update existing namespace
      const updatedNamespaces = [...dashboardData.namespaces];
      updatedNamespaces[existingIndex] = namespaceToAdd;
      setDashboardData(prev => ({
        ...prev,
        namespaces: updatedNamespaces
      }));
    } else {
      // Add new namespace
      setDashboardData((prev) => ({
        ...prev,
        namespaces: [...prev.namespaces, namespaceToAdd],
      }));
    }

    // Reset current namespace
    setCurrentNamespace({
      name: "",
      metrics: [],
      resourceNames: []
    });
    setCurrentAlarmConfigs({});

    setShowError(false);
  };

  // Remove namespace from dashboard
  const removeNamespace = (index) => {
    setDashboardData((prev) => ({
      ...prev,
      namespaces: prev.namespaces.filter((_, i) => i !== index),
    }));
  };

  // Toggle namespace collapse state
  const toggleNamespaceCollapse = (index) => {
    setCollapsedNamespaces(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  // Edit existing namespace
  const editNamespace = (namespace) => {
    setCurrentNamespace({
      name: namespace.name,
      metrics: [...namespace.metrics],
      resourceNames: [...namespace.resourceNames]
    });

    // Load existing alarm configs
    if (namespace.alarmConfigs) {
      setCurrentAlarmConfigs({ ...namespace.alarmConfigs });
    } else {
      setCurrentAlarmConfigs({});
    }

    const namespaceFormElement = document.getElementById("namespace-form");
    if (namespaceFormElement) {
      namespaceFormElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Next step in wizard
  const nextStep = () => {
  // Special validation for step 2 (Account & Region)
  if (currentStep === 2 && !isEditingDashboard) {
    const existingDashboard = checkDashboardExists(
      dashboardData.accountNo, 
      dashboardData.region
    );
    
    if (existingDashboard) {
      setShowError(true);
      setErrorMessage(`Dashboard already exists for this account and region combination. Existing dashboard: "${existingDashboard.name}"`);
      return;
    }
  }

  if (isCurrentStepValid()) {
    setCurrentStep((prev) => prev + 1);
    setShowError(false);
  } else {
    setShowError(true);
    setErrorMessage(getStepErrorMessage());
  }
};

  // Previous step in wizard
  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
    setShowError(false);
  };

  // Validate current step
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1:
        return dashboardData.name.trim() !== "";
      case 2:
        return dashboardData.accountNo !== "" && dashboardData.region !== "";
      case 3:
        return dashboardData.namespaces.length > 0;
      default:
        return true;
    }
  };

  // Get error message for current step
  const getStepErrorMessage = () => {
    switch (currentStep) {
      case 1:
        return "Please enter a dashboard name";
      case 2:
        return "Please select both AWS account and region";
      case 3:
        return "Please add at least one namespace configuration";
      default:
        return "Please check all required fields";
    }
  };

  // Get the minimum step for navigation
  const getMinStep = () => 1;
  const getMaxStep = () => 4;

  return (
    <div className="flex h-full bg-gray-50">
      {/* Sidebar */}
      <div className="w-72 bg-white p-8 border-r border-gray-200 shadow-sm">
        <h2 className="text-xl font-bold text-[#003366] mb-8">
          {isAddingDashboard ? "Create Dashboard" : "Edit Dashboard"}
        </h2>
        <div className="space-y-6">
          {steps.map((s) => (
            <div
              key={s.id}
              className={`flex items-center space-x-3 transition-colors duration-200
              ${currentStep === s.id ? "text-[#003366]" : "text-gray-500"}`}
            >
              {currentStep > s.id ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : currentStep === s.id ? (
                <div className="w-6 h-6 rounded-full bg-[#003366] flex items-center justify-center text-white text-sm">
                  {s.id}
                </div>
              ) : (
                <Circle className="w-6 h-6 text-gray-400" />
              )}
              <span
                className={`font-medium ${currentStep === s.id ? "text-[#003366]" : ""}`}
              >
                {s.title}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-y-auto">
        {/* Error Message */}
        {showError && (
          <div className="flex items-center p-4 mb-6 rounded-lg border bg-red-50 border-red-200 text-red-700">
            <AlertTriangle className="w-5 h-5 mr-3" />
            <span className="flex-1">{errorMessage}</span>
            <button
              type="button"
              onClick={() => setShowError(false)}
              className="ml-3 hover:opacity-70"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className="max-w-3xl mx-auto space-y-8">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <BasicInfoStep
              dashboardData={dashboardData}
              handleDashboardDataChange={handleDashboardDataChange}
              isEditingDashboard={isEditingDashboard}
            />
          )}

          {/* Step 2: Account & Region */}
          {currentStep === 2 && (
            <AccountRegionStep
              dashboardData={dashboardData}
              handleDashboardDataChange={handleDashboardDataChange}
              regions={regions}
              awsAccounts={awsAccounts}
              isEditingDashboard={isEditingDashboard}
            />
          )}

          {/* Step 3: Namespaces & Metrics */}
          {currentStep === 3 && (
            <NamespacesStep
              currentNamespace={currentNamespace}
              handleNamespaceChange={handleNamespaceChange}
              isLoadingNamespaces={isLoadingNamespaces}
              availableNamespaces={availableNamespaces}
              isLoadingMetrics={isLoadingMetrics}
              availableMetrics={availableMetrics}
              handleMetricToggle={handleMetricToggle}
              isLoadingResourceNames={isLoadingResourceNames}
              availableResourceNames={availableResourceNames}
              handleResourceNameToggle={handleResourceNameToggle}
              addNamespaceToDashboard={addNamespaceToDashboard}
              dashboardData={dashboardData}
              removeNamespace={removeNamespace}
              collapsedNamespaces={collapsedNamespaces}
              toggleNamespaceCollapse={toggleNamespaceCollapse}
              editNamespace={editNamespace}
              setDashboardData={setDashboardData}
              currentAlarmConfigs={currentAlarmConfigs}
              updateAlarmConfig={updateAlarmConfig}
            />
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <ReviewStep
              dashboardData={dashboardData}
              awsAccounts={awsAccounts}
              regions={regions}
              collapsedNamespaces={collapsedNamespaces}
              toggleNamespaceCollapse={toggleNamespaceCollapse}
              isEditingDashboard={isEditingDashboard}  // ADD THIS LINE
            />
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            <div className="flex gap-3">
              {currentStep > getMinStep() && (
                <button
                  onClick={prevStep}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
              )}

              {currentStep < getMaxStep() ? (
                <button
                  onClick={nextStep}
                  disabled={!isCurrentStepValid()}
                  className="px-4 py-2 bg-[#003366] text-white rounded-md hover:bg-[#004488] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={onSave}
                  className="px-4 py-2 bg-[#003366] text-white rounded-md hover:bg-[#004488] transition-colors"
                >
                  {isEditingDashboard ? "Save Changes" : "Create Dashboard"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}